import React from 'react';

import { useQuery } from '@tanstack/react-query';
import debounce from 'debounce-promise';
import { partial } from 'ramda';
import { GroupBase, OptionProps } from 'react-select-5';
import { AsyncProps } from 'react-select-5/async';

import { fetchPages, IMasterProperty, TParams } from 'api/pages';
import { queryClient } from 'providers/AppQueryClientProvider';

import { AsyncSelect } from '../../AsyncSelect';
import { usePreviewModal, useStateManager } from './helpers';
import { PageOption } from './PageOption';

export const PageAsyncSelect = function <
  Option extends IMasterProperty = IMasterProperty,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ params, ...props }: Partial<AsyncProps<Option, IsMulti, Group>> & { params?: TParams }) {
  const previewModal = usePreviewModal();
  const stateManager = useStateManager({ ...props, isModalOpenned: previewModal.isOpen });

  const pagesQuery = useQuery({
    queryKey: ['pages', params, ''],
    queryFn: partial(fetchPages, ['', params]),
  });

  const loadOptions = React.useMemo(() => {
    return debounce((req: string): Promise<Option[]> => {
      return queryClient.fetchQuery({
        queryKey: ['pages', params?.statuses ?? [], req],
        queryFn: partial(fetchPages, [req, params]),
      });
    }, 250);
  }, [params]);

  const renderPageOption = React.useCallback(
    (optionProps: OptionProps<Option, IsMulti, Group>) => (
      <PageOption {...optionProps} handlePreview={previewModal.show} />
    ),
    [],
  );

  return (
    <AsyncSelect<Option, IsMulti, Group>
      isSortable
      defaultOptions={(pagesQuery.data ?? []) as Option[]}
      cacheOptions={false}
      closeMenuOnSelect={false}
      loadOptions={loadOptions}
      isLoading={pagesQuery.isFetching}
      getOptionLabel={(option) => option.name ?? option.handle}
      getOptionValue={(option) => option.id?.toString()}
      components={{ Option: renderPageOption }}
      noOptionsMessage={() => 'No pages found'}
      {...stateManager}
    />
  );
};
